
import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Search, FileText, ArrowRight, ShieldAlert } from 'lucide-react';
import { CLASSES } from '../constants';

const NotesHub: React.FC = () => {
  return (
    <div className="bg-gray-50 min-h-screen pb-24">
      <section className="bg-white py-20 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6">Resource Repository</h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Explore our structured library of study materials. We have organized everything by class and subject to ensure you spend more time studying and less time searching.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        {/* Important Notice */}
        <div className="bg-blue-600 text-white p-8 rounded-3xl shadow-xl flex flex-col md:flex-row items-center gap-8 mb-16">
          <div className="bg-white/20 p-4 rounded-full">
            <ShieldAlert size={40} className="text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold mb-2">Note: Sample Access Only</h2>
            <p className="text-blue-100 text-lg">Currently, we are providing highly structured sample notes to maintain quality. The full digital textbook compilation is in progress and will be available for all subjects shortly.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Main Navigation Grid */}
          <div className="lg:col-span-9 space-y-12">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-8">Select Your Level to View Notes</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {CLASSES.map((cls) => (
                  <Link key={cls.id} to={`/class/${cls.id}`} className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition group">
                    <div className="flex items-center justify-between mb-6">
                      <div className="bg-blue-50 text-blue-600 p-4 rounded-xl group-hover:bg-blue-600 group-hover:text-white transition">
                        <BookOpen size={24} />
                      </div>
                      <span className="text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">{cls.subjects.length} Subjects</span>
                    </div>
                    <h4 className="text-2xl font-bold text-slate-900 mb-2">{cls.name}</h4>
                    <p className="text-slate-500 mb-6">Access sample notes for all primary and elective subjects of {cls.name}.</p>
                    <div className="flex items-center text-blue-600 font-bold">
                      Browse Subjects <ArrowRight size={18} className="ml-2" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="bg-white p-10 rounded-3xl border border-gray-100">
              <h3 className="text-2xl font-bold text-slate-900 mb-8">How Our Notes are Organized</h3>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="shrink-0 w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center font-bold">1</div>
                  <div>
                    <h5 className="font-bold text-slate-900 text-lg">Subject Cataloging</h5>
                    <p className="text-slate-600">Every subject is listed clearly with its specific All Board curriculum code.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="shrink-0 w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center font-bold">2</div>
                  <div>
                    <h5 className="font-bold text-slate-900 text-lg">Topic-wise Breakdown</h5>
                    <p className="text-slate-600">Notes are divided into Definitions, Short Questions, Long Questions, and MCQs.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="shrink-0 w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center font-bold">3</div>
                  <div>
                    <h5 className="font-bold text-slate-900 text-lg">PDF Compilation</h5>
                    <p className="text-slate-600">All samples are optimized for mobile and desktop reading in lightweight PDF format.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-3 space-y-8">
            <div className="bg-slate-900 text-white p-8 rounded-3xl shadow-xl">
              <FileText className="text-blue-400 mb-6" size={40} />
              <h4 className="text-xl font-bold mb-4">Request Notes</h4>
              <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                Need notes for a specific subject not listed here? Our team might have them ready!
              </p>
              <Link to="/contact" className="block text-center bg-blue-600 py-3 rounded-xl font-bold hover:bg-blue-700 transition">Contact Us</Link>
            </div>
            
            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
              <h4 className="text-lg font-bold text-slate-900 mb-4">Upcoming Updates</h4>
              <ul className="space-y-4 text-sm text-slate-600">
                <li className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                  <span>Chemistry Full Notes (Class 10)</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                  <span>Physics Numerical Guide (1st Year)</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                  <span>Pak Studies Urdu Medium (All Classes)</span>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default NotesHub;
